/**
 * Make a picture weigh less without changing the design.
 *
 * A template carries its images inside it, as data URLs in the document, so
 * every megabyte of photo is a megabyte in every save, every load and every
 * export — and a phone camera's 4000px portrait dropped into a 120px passport
 * box is carrying about ninety-five percent of its pixels for nothing.
 *
 * Two things are done about that, in this order:
 *   1. downscale to what the layer can actually print (its box at print DPI,
 *      never upscaled)
 *   2. re-encode as WebP, which is smaller than both PNG and JPEG at the same
 *      quality and, unlike JPEG, keeps transparency — a cut-out photo must not
 *      come back with a white box behind it
 *
 * If neither wins any bytes the original is kept: an image already smaller than
 * what would be produced is left exactly as it is, rather than re-encoded into
 * something larger with generation loss on top.
 */
import { fetchMediaAsDataUrl, isTemplateMediaUrl } from './templateMedia';

/** Screen px → print px. A 72dpi box printed at 300dpi needs this much more. */
const PRINT_SCALE = 4;
const MIN_SIDE = 640;
const MAX_SIDE = 2400;
const QUALITY = 0.82;
/**
 * Under this much saved, re-encoding is not worth the generation loss. Set at a
 * tenth after watching a second pass over an already-optimised photo scrape 5%
 * by throwing away detail — a trade nobody asked for.
 */
const MIN_GAIN = 0.1;

/** Bytes a data URL actually costs. Null for anything else — it is not local. */
export function dataUrlBytes(src) {
    const s = String(src || '');
    const comma = s.indexOf(',');
    if (!s.startsWith('data:') || comma < 0) return null;
    const body = s.slice(comma + 1);
    if (!/;base64/i.test(s.slice(0, comma))) return body.length;
    const padding = body.endsWith('==') ? 2 : body.endsWith('=') ? 1 : 0;
    return Math.max(0, Math.floor((body.length * 3) / 4) - padding);
}

/**
 * The longest side worth keeping for a layer of this size — what it can show at
 * print resolution, and no more. Clamped at both ends: a tiny stamp still keeps
 * enough pixels to be re-used bigger later, and a full-bleed background does not
 * get to keep 6000px.
 */
export function targetSideFor(el) {
    const box = Math.max(Number(el?.width) || 0, Number(el?.height) || 0);
    return Math.round(Math.min(MAX_SIDE, Math.max(MIN_SIDE, box * PRINT_SCALE)));
}

/**
 * What a picture WILL cost the template, whether or not it is embedded yet.
 *
 * A saved template's images have been lifted into R2 and the document holds a
 * short URL, so there are no local bytes to count — but the number the panel
 * needs is still the size of the picture, so it is fetched (through our own API,
 * which is also the only way we are allowed to read it) and measured. Cached by
 * URL: the answer cannot change, and the fetch is a whole photo.
 */
const measured = new Map();
export async function measureSrcBytes(src) {
    if (!src) return null;
    const local = dataUrlBytes(src);
    if (local != null) return local;
    if (measured.has(src)) return measured.get(src);
    if (!isTemplateMediaUrl(src)) return null;
    try {
        const bytes = dataUrlBytes(await fetchMediaAsDataUrl(src));
        measured.set(src, bytes);
        return bytes;
    } catch {
        measured.set(src, null);
        return null;
    }
}

function decode(src, { cors = false } = {}) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        if (cors) img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('Could not read that image'));
        img.src = src;
    });
}

/**
 * The picture as pixels this browser is allowed to READ.
 *
 * A saved template's images live on another origin (see utils/templateMedia) and
 * drawing one straight onto a canvas taints it, so `toDataURL` would throw. Same
 * answer the exporter reached: fetch the bytes through our own API instead.
 */
async function loadReadable(src) {
    if (/^(data:|blob:)/i.test(src)) return decode(src);
    try {
        return await decode(src, { cors: true });
    } catch (err) {
        if (!isTemplateMediaUrl(src)) throw err;
        return decode(await fetchMediaAsDataUrl(src));
    }
}

/** Does this picture use transparency? Sampled, because a full scan is slow. */
function hasAlpha(canvas, ctx) {
    const step = Math.max(1, Math.floor(Math.max(canvas.width, canvas.height) / 256));
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let y = 0; y < canvas.height; y += step) {
        for (let x = 0; x < canvas.width; x += step) {
            if (data[(y * canvas.width + x) * 4 + 3] < 250) return true;
        }
    }
    return false;
}

/**
 * A smaller version of `src`, or null when there is nothing to win.
 *
 * `bytes` on the way in is what the original costs; it is measured here for a
 * data URL and, for a stored one, taken from the bytes that were fetched — the
 * comparison has to be against what the DOCUMENT will carry, which is always a
 * data URL by the time it is saved.
 */
export async function optimizeImageSrc(src, { maxSide = MAX_SIDE, quality = QUALITY } = {}) {
    if (!src) return null;
    const img = await loadReadable(src);
    const natural = { w: img.naturalWidth || img.width, h: img.naturalHeight || img.height };
    if (!natural.w || !natural.h) return null;

    // Never upscale: enlarging a small picture invents pixels and costs bytes.
    const scale = Math.min(1, maxSide / Math.max(natural.w, natural.h));
    const width = Math.max(1, Math.round(natural.w * scale));
    const height = Math.max(1, Math.round(natural.h * scale));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, 0, 0, width, height);

    const before = dataUrlBytes(src) ?? dataUrlBytes(img.src);
    const candidates = [];
    const webp = canvas.toDataURL('image/webp', quality);
    // A browser without WebP hands back a PNG instead of refusing.
    if (webp.startsWith('data:image/webp')) candidates.push(webp);
    // JPEG is the fallback, and only for pictures with nothing to keep
    // transparent — it would flatten a cut-out onto black.
    if (!candidates.length && !hasAlpha(canvas, ctx)) {
        candidates.push(canvas.toDataURL('image/jpeg', quality));
    }
    if (!candidates.length) candidates.push(canvas.toDataURL('image/png'));

    const best = candidates
        .map((out) => ({ src: out, bytes: dataUrlBytes(out) || Infinity }))
        .sort((a, b) => a.bytes - b.bytes)[0];

    if (!best || !Number.isFinite(best.bytes)) return null;
    // Only worth doing if it actually saves something worth the generation loss.
    // A stored image has no local "before" to beat, so any result is a win — it
    // replaces bytes the document does not hold yet.
    if (before != null && best.bytes > before * (1 - MIN_GAIN)) return null;

    return {
        src: best.src,
        bytes: best.bytes,
        before,
        width,
        height,
        naturalWidth: natural.w,
        naturalHeight: natural.h,
        resized: scale < 1,
    };
}
